import type React from 'react';

import { Text } from '@change/ds-core-ui/Text';
import { TextArea, TextField } from '@change/ds-forms/TextField';
import { SparkleIcon } from '@change/ds-icons/SparkleIcon';

import { TypedTranslate, useI18n } from '@change-corgi/core-react/i18n';

import { seedDraft } from '#src/app/shared/utils/contactDecisionMaker';

import { ComposeSheet } from '../ComposeSheet';
import { findReasonCard } from '../reasons';
import type { ContactDecisionMakerStageProps } from '../types';

/**
 * Screen 3, the story (CHANGE-95795). Shows the talking points the chosen reason calls for,
 * then asks for the supporter's own words.
 *
 * The supporter's text is what the draft is built around — research found that ~1/3 of testers worried
 * a flood of identical AI messages would dilute everyone's impact, so this textarea is the part that
 * has to carry weight.
 */
export const StoryWrite: React.FC<ContactDecisionMakerStageProps> = ({ decisionMaker, petitionTitle, flow }) => {
	const { translate, typedTranslate } = useI18n();
	const { reasonId, story } = flow.data;
	const card = reasonId ? findReasonCard(reasonId) : undefined;

	const canDraft = story.trim().length > 0;

	return (
		<ComposeSheet
			isOpen
			fullHeightOnMobile
			onOpenChange={(isOpen) => {
				if (!isOpen) flow.actions.dismiss();
			}}
			// The heading narrows to the picked reason. `card` is only ever undefined if this stage were
			// reached without a pick, which the machine does not allow — screen 2's heading is the
			// honest fallback rather than an empty one.
			heading={
				card
					? typedTranslate(card.headingKey)
					: translate('corgi.contact_decision_maker.story.heading', {
							decision_maker_name: decisionMaker.displayName,
						})
			}
			back={{
				onClick: flow.actions.backToReasons,
				ariaLabel: translate('corgi.contact_decision_maker.story.back'),
			}}
			primaryAction={{
				label: translate('corgi.contact_decision_maker.story.draft_cta'),
				leadingIcon: <SparkleIcon />,
				onClick: flow.actions.requestDraft,
				// Trimmed, so a textarea holding only whitespace does not count as a story.
				disabled: !canDraft,
			}}
			tertiaryAction={{
				label: translate('corgi.contact_decision_maker.story.write_own_cta'),
				onClick: () => flow.actions.writeOwnDraft(seedDraft(translate, decisionMaker.displayName, story)),
				// Same guard as the primary. This path skips drafting and seeds the review screen from the
				// story, and `seedDraft` wraps it in a salutation — so a blank story yields "Dear X,\n\n\n\n",
				// which is non-empty after trimming and clears Preview's own empty-draft guard. Send would
				// then be enabled for a message that is nothing but the salutation.
				disabled: !canDraft,
			}}
		>
			{card && (
				<div
					className="border-stroke-info-weak bg-fill-information-weaker flex flex-col gap-2 rounded-md border border-solid px-4 py-3"
					data-testid="contact-decision-maker-reason-recap"
				>
					<Text as="span" className="text-size-body-xs-bold text-typography-information tracking-[0.05em] uppercase">
						{translate('corgi.contact_decision_maker.story.points_label')}
					</Text>
					<ul className="text-size-body-small text-typography-strong m-0 flex list-disc flex-col gap-1 pl-5">
						{/* Keyed by position: `TranslationKey` is opaque by design, and the tuple is fixed-length
						    and never reordered. */}
						{card.pointsKeys.map((pointKey, index) => (
							<li key={index}>
								<TypedTranslate value={pointKey} />
							</li>
						))}
					</ul>
				</div>
			)}

			{/* Serif on purpose: it is the same face the draft is shown in on the next screen, so what the
			    supporter types already looks like the letter it becomes. */}
			<TextField
				label={translate('corgi.contact_decision_maker.story.story_label')}
				labelIsVisible={false}
				name="contact-decision-maker-story"
				className="mt-3"
				value={story}
				onChange={flow.actions.setStory}
				placeholder={card ? typedTranslate(card.placeholderKey, { petition_title: petitionTitle }) : undefined}
				supportingText={translate('corgi.contact_decision_maker.story.draft_helper')}
				input={<TextArea className="min-h-[220px] resize-none font-serif" />}
			/>
		</ComposeSheet>
	);
};
